import { useEffect, useState } from 'react';
import {
	Button,
	TextField,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Alert,
	AlertTitle
} from '@mui/material';

import ReCAPTCHA from "react-google-recaptcha";
import axios from 'axios';
import { getClientSettings } from '../../utilities/utilities';

const ForgotCredentials = () => {
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [isEpiqEmail, setIsEpiqEmail] = useState(false);
	const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
	const [captchaToken, setCaptchaToken] = useState(null);
	const [reCaptchaSiteKey, setReCaptchaSiteKey] = useState('invalid_site_key');
	const [canReset, setCanReset] = useState(false);

	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const response = await axios.get('/api/captcha/enabled');
				setIsCaptchaEnabled(response.data);
			} catch (error) {
				throw new Error('Failed to determine reCaptcha availability.');
			}

			const clientSettings = await getClientSettings();
			setReCaptchaSiteKey(clientSettings.reCaptchaSiteKey);
		};

		fetchConfig();
	}, []);

	useEffect(() => {
		if (!email || !captchaToken || !!emailError) {
			setCanReset(false);
			return;
		}

		if (isEpiqEmail) {
			setCanReset(false);
			return;
		}

		setCanReset(true);
	}, [email, emailError, captchaToken, isEpiqEmail]);

	const validateEmail = (emailToValidate: string) => {
		const validRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,3}))$/;
		return validRegex.test(emailToValidate);
	};

	const handleEmailValidation = () => {
		if (email && !validateEmail(email)) {
			setEmailError('Please enter a valid email address');
		} else {
			setEmailError('');
		}

		const epiqEmailRegex = /@.*epiq.*\.\w{2,63}$/;

		// if there are no email errors and the user name part of the email address contains "epiq", then set isEpiqEmail to true
		if (!emailError && email && epiqEmailRegex.test(email)) {
			setIsEpiqEmail(true);
		} else {
			setIsEpiqEmail(false);
		}
	};

	const handleCaptchaVerify = async (token: any) => {
		setCaptchaToken(token);
	};

	interface ISubmitMessage { message: string };

	const [submitMessage, setSubmitMessage] = useState<ISubmitMessage | null>(null);

	function sleep(ms: number) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	const handleSubmit = async (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		if (isEpiqEmail) {
			alert('Resetting Epiq passwords is not supported.');
			return;
		}

		try {
			let response = await axios.post('/api/auth/forgotCredentials', {
				email: email,
				captchaToken: captchaToken
			});

			setSubmitMessage({ message: response.data.message });
			await sleep(3000);	// give the user 3 seconds to read the success alert
			window.location.href = `/`;
		} catch (error) {
			console.error('Error:', error);
		}
	};

	const handleClose = (event: any, reason: string) => {
		event.preventDefault();

		if (reason === 'escapeKeyDown') {
			window.location.href = `/`;
		}
	};

	const handleCancel = (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		window.location.href = `/`;
	};

	return (
		<Dialog open={true} onClose={handleClose}>
			<DialogTitle>Forgot Password</DialogTitle>
			<DialogContent>
				<Grid
					container
					spacing={2}
					direction="column"
					justifyContent="center"
					alignItems="center"
				>
					<Grid item xs={12}>
						<Typography variant="h5">
							To reset your password please enter your email
						</Typography>

						<TextField
							autoFocus
							fullWidth
							label="Email"
							variant='standard'
							InputLabelProps={{
								shrink: true,
								style: {
									color: "black",
								},
							}}
							margin="normal"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onBlur={handleEmailValidation}
							error={!!emailError}
							helperText={emailError}
							required
						/>
					</Grid>

					<Grid item xs={12}>
						{isCaptchaEnabled && (
							<ReCAPTCHA
								sitekey={reCaptchaSiteKey}
								onChange={handleCaptchaVerify}
							/>
						)}
					</Grid>

					{submitMessage?.message &&
						<Grid item xs={12}>
							<Alert severity="success" variant="filled">
								<AlertTitle>Success</AlertTitle>
								{submitMessage?.message}
							</Alert>
						</Grid>
					}
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button
					fullWidth
					variant="contained"
					type="button"
					onClick={handleCancel}
					sx={{
						"&:focus": {
							backgroundColor: "#253746 !important", // Keep the default background color on focus
						},
					}}
				>
					Cancel
				</Button>

				<Button
					fullWidth
					variant="contained"
					type="button"
					onClick={handleSubmit}
					disabled={!canReset}
					sx={{
						"&:focus": {
							backgroundColor: "#253746 !important", // Keep the default background color on focus
						},
					}}
				>
					Reset
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ForgotCredentials;
